
import { defineComponent } from "vue";
import { Alert } from "@/types/alert.type";
import { Filter } from "@/types/filter.type";
import AlertFormComponent from "@/components/alerts/AlertForm.component.vue";
import { changeAlertActive, deleteAlert, getAlertPage } from "@/service/alert.service";
import { Action, ElMessage, ElMessageBox } from "element-plus";
import NoDataComponent from "@/components/NoData.component.vue";
import ObserverComponent from "../Observer.component.vue";
import { getSamplingPointNames } from "@/service/paramaeterAlert.service";
import { User } from "@/types/user.type";
import { InfoFilled } from "@element-plus/icons-vue";

const successAction = "success.action";
export default defineComponent({
  components: {
    // PageHeaderComponent,
    AlertFormComponent,
    // SamplingPointTagComponent,
    NoDataComponent,
    ObserverComponent,
  },
  props: {
    detailRouterName: {
      type: String,
    },
    list: {
      type: Object as () => Alert[],
    },
  },
  data() {
    return {
      currentView: 1 as number,
      query: "" as string,
      alerts: [] as Alert[],
      hasData: true,
      showAlertForm: false,
      showAlertInfo: false,
      alertSelected: undefined as undefined | Alert,
      currentPage: 1 as number,
      rowPerPage: 10 as number,
      totalRows: 0 as number,
      showEntityForm: false,
      samplingPointNames: {} as Record<number, string>,
    };
  },
  mounted() {
    this.refreshAlert();

    if (this.$route.query.create == "true") {
      this.currentView = 1;
      this.showAlertForm = true;
    }
  },
  methods: {
    async handleScroll() {
      const totalPages = Math.ceil(this.totalRows / this.rowPerPage);
      if (this.currentPage < totalPages) {
        this.currentPage++;

        const filter: Filter = {};
        if (this.query) {
          filter.multi = this.query;
        }

        const pagination = await getAlertPage(this.currentPage, this.rowPerPage, filter);

        // Agregar nuevas alertas a la lista
        this.alerts.push(...pagination.data);

        // Obtener nombres de puntos para las nuevas alertas
        for (const alert of pagination.data) {
          if (alert.id !== undefined) {
            this.getSamplingPointName(alert).then((name) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.samplingPointNames[alert.id!] = name ?? "";
            });
          }
        }
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getNotificationMethods(item: any) {
      const methods = [];

      if (item.active) methods.push("WEAS");
      if (item.email) methods.push("<i>E-mail</i>");
      if (item.sms) methods.push("WhatsApp");

      if (methods.length > 1) {
        return `${methods.join(" | ")}`;
      }

      return methods.join("");
    },

    goToAlerts(optionalParam = "") {
      let basePath = "";

      if (this.$route.path.includes("admin")) {
        basePath = "/admin/generatedAlerts";
      } else if (this.$route.path.includes("stations")) {
        basePath = "/stations/generatedAlerts";
      } else {
        basePath = "/support/generatedAlerts";
      }

      const fullPath = optionalParam ? `${basePath}?id=${optionalParam}` : basePath;

      this.$router.push(fullPath);
    },

    goToNextView() {
      this.currentView++;
    },
    goToPreviousView() {
      this.currentView--;
    },
    closeAlertDialog() {
      this.currentView = 1;
      this.showAlertForm = false;
      this.showAlertInfo = false;
      this.alertSelected = undefined;
    },
    async refreshAlert(item: Alert | undefined = undefined) {
      let index = -1;
      if (this.alerts && item) {
        index = this.alerts.findIndex((i) => i.id === item.id);
      }
      if (index !== -1 && item) {
        this.alerts[index] = item;
      } else {
        const filter: Filter = {};
        if (this.query) {
          filter.multi = this.query;
        }
        this.currentPage = 1;
        const pagination = await getAlertPage(this.currentPage, this.rowPerPage, filter);
        this.alerts = pagination.data;
        this.totalRows = pagination.totalRows;
      }
      if (this.alerts.length <= 0) {
        this.hasData = false;
        return;
      } else {
        this.hasData = true;
      }
      for (const alert of this.alerts) {
        if (alert.id !== undefined) {
          this.getSamplingPointName(alert).then((name) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.samplingPointNames[alert.id!] = name ?? "";
          });
        }
      }

      return this.alerts;
    },
    changePaginationSize(size: number): void {
      this.rowPerPage = size;
      this.refreshAlert();
    },

    changePaginationPage(page: number): void {
      this.currentPage = page;
      this.refreshAlert();
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async saveAlert(_alertSelected: Alert | undefined) {
      // - call alertForm save method
      const alert = await (
        this.$refs["alertForm"] as InstanceType<typeof AlertFormComponent>
      ).doSave();
      if (alert) {
        // - close dialog and refresh
        this.closeAlertDialog();
        this.refreshAlert(alert);

        ElMessage.success(this.$t(successAction));
      }
    },
    async getUserAlerts(user: User) {
      let allAlerts: Alert[] = [];
      let page = 1;
      const limit = 50;
      let moreData = true;

      while (moreData) {
        const response = await getAlertPage(page, limit);
        if (response.data.length > 0) {
          allAlerts = [...allAlerts, ...response.data];
          page++;
        } else {
          moreData = false;
        }
      }

      const userAlerts = allAlerts.filter(
        (alert) => alert.sms === true && alert.user?.id === user.id && alert.active
      );

      return (
        user.alertCreationLimit !== undefined &&
        user.alertCreationLimit !== null &&
        userAlerts.length >= user.alertCreationLimit
      );
    },
    async deactivateAlert(item: Alert, newValue: boolean) {
      if (item.id && item.active !== undefined && item.user) {
        if ((await this.getUserAlerts(item.user)) && item.sms && newValue) {
          ElMessage.warning({
            message:
              "No se puede activar la alerta porque se ha alcanzado el límite de envíos por WhatsApp. Por favor, edite la alerta para desactivar el envío de WhatsApp antes de reactivar esta alerta o quite dicha configuración en otra alerta.",
            duration: 8000,
            showClose: true,
          });
          return;
        }

        const typeMessage = newValue ? "activateAlert" : "deactivateAlert";

        let message = this.$t("confirm." + typeMessage, { name: item.name });

        if (!newValue && item.triggers && item.sendEmail) {
          message = message + " <br/>" + this.$t("confirm.deactivateAlertStarted");
        }
        message = message + this.$t("confirm.askSave");

        ElMessageBox.confirm(message, this.$t("label.warning"), {
          confirmButtonText: this.$t("btn.accept"),
          cancelButtonText: this.$t("btn.cancel"),
          type: "warning",
          dangerouslyUseHTMLString: true,
          callback: async (action: Action) => {
            if (action === "confirm" && item.id) {
              try {
                const pointId = item.parameterAlerts
                  ? item.parameterAlerts[0]?.samplingPoint?.id
                  : undefined;
                await changeAlertActive(item.id, newValue, pointId);
                item.active = newValue;
                ElMessage.success(this.$t(successAction));
                this.refreshAlert();
              } catch (error) {
                ElMessage.error("No se pudo cambiar el estado de la alerta.");
              }
            } else {
              item.active = !newValue;
            }
          },
        });
      }
    },

    /**
     * open alert formDialog
     * @param item
     */
    openEntityFormDialog(item: Alert | undefined = undefined) {
      this.alertSelected = item;
      this.showAlertForm = true;
    },

    deleteAlert(item: Alert | undefined = undefined) {
      if (item && item.id) {
        ElMessageBox.confirm(
          this.$t("confirm.deleteAlert", { name: item.name }),
          this.$t("label.warning"),
          {
            confirmButtonText: this.$t("btn.accept"),
            cancelButtonText: this.$t("btn.cancel"),
            type: "warning",
            dangerouslyUseHTMLString: true,
            callback: (action: Action) => {
              if (action === "confirm") {
                deleteAlert(item).then(() => {
                  ElMessage.success(this.$t(successAction));
                  this.refreshAlert();
                });
              }
            },
          }
        );
      }
    },
    async getSamplingPointName(alert: Alert) {
      const alertData = await getSamplingPointNames(alert);
      if (alertData) {
        return alertData[0].name;
      }
      return "-";
    },
  },
});
